import { Injectable, inject } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TransportMean, TransportMeanIconName } from '../../../../interfaces/register-user.response';
import { normalizeValue } from '../../../../utils/value-normalizer';
import { RegisterUserFormService } from '../../../../services/register-user-form.service';
import { rawTransportMeans } from '../config/constants';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { closeAllCiSnackbar } from '@iurban-malaga/cicerone-ui';
import { FormStepperService } from '../../../../components/register-user-form-mobile/services';

@Injectable({
  providedIn: 'root'
})
export class TransportMeansFieldService {
  // Services
  private _registerUserFormService = inject(RegisterUserFormService);
  private _formStepperService = inject(FormStepperService);
  private _breakpointsService = inject(BreakpointsService);

  // Signals
  private _currentTransportMeans = this._registerUserFormService.transportMeans;
  private _markAllFormFieldsAsTouched = this._registerUserFormService.markAllFormFieldsAsTouched;
  private _isLastSlideValidationActive = this._formStepperService.isLastSlideValidationActive;
  private _isMobile = this._breakpointsService.isMobile;

  // Methods
  /**
   * Gets the transport means options with normalized values and proper configuration
   * Maintains the exact same logic as the original component
   * @returns Array of transport means with all required properties
   */
  getTransportMeansOptions(): TransportMean[] {
    return rawTransportMeans.map((transport, index) => {
      return {
        id: index,
        label: transport.label,
        value: normalizeValue(transport.label),
        iconName: transport.iconName.trim() as TransportMeanIconName,
        eco: transport.eco,
      }
    });
  }

  /**
   * Handles the chip set transport means change event
   * Maintains the exact same logic as the original component
   * @param selectedTransportMeans - Array of selected transport means values
   * @param control - The form control to update
   */
  handleTransportMeansSelection(selectedTransportMeans: string[], control: AbstractControl | null): void {
    // Exact same logic as original component
    this._registerUserFormService.setTransportMeans(selectedTransportMeans);
    control?.setValue(selectedTransportMeans);
    
    control?.markAsTouched();
    control?.markAsDirty();
    
    if (this._isMobile()) {
      this._registerUserFormService.setMarkAllFormFieldsAsTouched(false);
      closeAllCiSnackbar();
    }
  }

  /**
   * Checks if a specific transport mean is currently selected
   * Maintains the exact same logic as the original component
   * @param value - Transport mean value to check
   * @returns True if the transport mean is selected
   */
  isSelectedTransportMean(value: string): boolean {
    return this._currentTransportMeans().includes(value);
  }

  /**
   * Validates if the field has errors and should display them
   * Maintains the exact same validation logic as the original component
   * @param control - The form control to validate
   * @returns True if field is invalid and should show errors
   */
  validateField(control: AbstractControl | null): boolean {
    if (!control) return false;
    
    if (this._isMobile()) {
      // mobile: check only when last slide validation is active (exact same logic as original)
      if (this._isLastSlideValidationActive()) {
        return control?.hasError('required') && (control?.touched || control?.dirty);
      } else {
        return false;
      }
    } else {
      // desktop: normal behavior (exact same logic as original)
      return control?.hasError('required') && (control?.touched || control?.dirty);
    }
  }

  /**
   * Marks form fields as touched for desktop validation
   * Maintains the exact same logic as the original component
   * @param control - The form control to mark as touched
   * @param cdr - Change detector reference for manual detection
   */
  markFieldsAsTouchedForDesktop(control: AbstractControl | null, cdr: any): void {
    if (this._markAllFormFieldsAsTouched()) {
      if (!control) return;
      
      // En mobile: NO marcar automáticamente como touched para evitar errores iniciales
      // Solo se marcará como touched cuando el usuario presione "Continuar" sin seleccionar
      // (exact same logic as original)
      if (this._isMobile()) {
        return;
      }
      
      control?.markAsTouched();
      control?.markAsDirty();

      // Trigger change detection manually for OnPush (exact same as original)
      cdr.detectChanges();
    }
  }
}
